import React, { useState, useEffect } from 'react';
import { Mail, Send, Loader2, RotateCw, Search, Calendar, X, Eye, MousePointerClick, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from './ui/sonner';
import { getAuthHeaders, checkTokenExpiration } from '../lib/auth';
import { emailTemplates } from '../data/emailTemplates';
import EmailVisits from './EmailVisits';

const SentEmails = ({ refreshTrigger }) => {
  const [sentEmails, setSentEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCampaign, setFilterCampaign] = useState('');
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contactName, setContactName] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [visitsDialogOpen, setVisitsDialogOpen] = useState(false);
  const [selectedEmailForVisits, setSelectedEmailForVisits] = useState(null);

  useEffect(() => {
    fetchSentEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, filterCampaign, refreshTrigger]);

  const fetchSentEmails = async () => {
    setLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
      });
      
      if (searchTerm) {
        params.append('recipient', searchTerm);
      }
      if (filterCampaign) {
        params.append('campaignId', filterCampaign);
      }

      const response = await fetch(`${apiUrl}/api/campaigns/sent-emails?${params}`, {
        headers: getAuthHeaders(),
      });

      if (checkTokenExpiration(response)) {
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSentEmails(data.emails);
        setTotalPages(data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching sent emails:', error);
      toast.error('Failed to load sent emails');
    } finally {
      setLoading(false);
    }
  };

  const handleResendClick = (email) => {
    setSelectedEmail(email);
    setResendDialogOpen(true);
    setContactName(''); // Reset contact name
    // Pre-select the original template if available
    const originalTemplate = emailTemplates.find(t => t.campaignId === email.campaignId || t.id === email.templateId);
    if (originalTemplate) {
      setSelectedTemplate(originalTemplate);
    }
  };

  const handleResend = async () => {
    if (!selectedEmail || !selectedTemplate) {
      toast.error('Please select a campaign template');
      return;
    }

    setIsResending(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const frontendUrl = import.meta.env.VITE_FRONTEND_URL || 'https://axiosdevelopment.dev';
      
      // Replace campaign link with correct domain and campaign ID
      let htmlContent = selectedTemplate.html;
      if (selectedTemplate.campaignId) {
        htmlContent = htmlContent.replace(
          /(href=["'])([^"']*#contact\?c=[^"']+)(["'])/g,
          `$1${frontendUrl}/#contact?c=${selectedTemplate.campaignId}$3`
        );
      }

      // Replace contact name placeholder if template requires it
      if (selectedTemplate.requiresContactName) {
        if (contactName && contactName.trim()) {
          // If contact name is provided, replace with the name
          htmlContent = htmlContent.replace(/\{\{CONTACT_NAME\}\}/g, contactName.trim());
        } else {
          // If blank, replace with empty string
          htmlContent = htmlContent.replace(/\{\{CONTACT_NAME\}\}/g, '');
          // Clean up greeting patterns that result from empty replacement
          // "Hey, {{CONTACT_NAME}}!" -> "Hey, !" -> "Hey!"
          htmlContent = htmlContent.replace(/Hey,\s*!/g, 'Hey!');
          // "Hey, {{CONTACT_NAME}}," -> "Hey, ," -> "Hey,"
          htmlContent = htmlContent.replace(/Hey,\s*,/g, 'Hey,');
          // "Hello {{CONTACT_NAME}}," -> "Hello ," -> "Hello,"
          htmlContent = htmlContent.replace(/Hello\s+,/g, 'Hello,');
          // "Hello {{CONTACT_NAME}}!" -> "Hello !" -> "Hello!"
          htmlContent = htmlContent.replace(/Hello\s+!/g, 'Hello!');
        }
      }

      const response = await fetch(`${apiUrl}/api/campaigns/resend`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          emailId: selectedEmail.id,
          templateId: selectedTemplate.id,
          campaignId: selectedTemplate.campaignId,
          subject: selectedTemplate.subject,
          html: htmlContent,
        }),
      });

      if (checkTokenExpiration(response)) {
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend email');
      }

      toast.success('Email resent successfully!', {
        description: `Sent to ${selectedEmail.recipient}`,
      });

      // Close dialog and refresh
      setResendDialogOpen(false);
      setSelectedEmail(null);
      setSelectedTemplate(null);
      setContactName('');
      fetchSentEmails();
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error('Failed to resend email', {
        description: error.message || 'Please try again later.',
      });
    } finally {
      setIsResending(false);
    }
  };

  const getTemplateName = (campaignId, templateId) => {
    const template = emailTemplates.find(t => t.campaignId === campaignId || t.id === templateId);
    return template ? template.name : 'Unknown';
  };

  const getStatusBadge = (status) => {
    const variants = {
      sent: 'bg-green-500/20 text-green-400 border-green-500/30',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30',
      bounced: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    };
    return variants[status] || 'bg-[#3f4816] text-[#888680] border-[#3f4816]';
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="border-[#3f4816] bg-[#302f2c]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-[#888680]" />
              <Input
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
              />
            </div>
            <select
              value={filterCampaign}
              onChange={(e) => {
                setFilterCampaign(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 rounded-md bg-[#1a1c1b] border border-[#3f4816] text-white focus:border-[#d9fb06] focus:outline-none"
            >
              <option value="">All Campaigns</option>
              {emailTemplates.map(template => (
                <option key={template.campaignId} value={template.campaignId}>
                  {template.name}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              onClick={fetchSentEmails}
              className="border-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]"
            >
              <RotateCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sent Emails Table */}
      <Card className="border-[#3f4816] bg-[#302f2c]">
        <CardHeader>
          <CardTitle className="text-[#d9fb06]">Sent Emails</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#d9fb06] mx-auto" />
            </div>
          ) : sentEmails.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 text-[#888680] mx-auto mb-3" />
              <p className="text-[#888680]">No sent emails found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#3f4816]">
                      <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Recipient</th>
                      <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Campaign</th>
                      <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Subject</th>
                      <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Status</th>
                      <th className="text-center py-3 px-4 text-[#d9fb06] font-semibold">Opens</th>
                      <th className="text-center py-3 px-4 text-[#d9fb06] font-semibold">Clicks</th>
                      <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Sent</th>
                      <th className="text-right py-3 px-4 text-[#d9fb06] font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentEmails.map((email) => (
                      <tr key={email.id} className="border-b border-[#3f4816] hover:bg-[#1a1c1b]">
                        <td className="py-3 px-4 text-white">{email.recipient}</td>
                        <td className="py-3 px-4">
                          <span className="text-[#888680] text-sm">
                            {getTemplateName(email.campaignId, email.templateId)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-white text-sm max-w-xs truncate">
                          {email.subject}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusBadge(email.status)}>
                            {email.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <Eye className="h-3 w-3 text-[#888680]" />
                            <span className="text-white font-medium">{email.openCount || 0}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <MousePointerClick className="h-3 w-3 text-[#888680]" />
                            <span className="text-white font-medium">{email.clickCount || 0}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-[#888680] text-sm">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(email.sentAt).toLocaleDateString()}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedEmailForVisits(email);
                                setVisitsDialogOpen(true);
                              }}
                              className="border-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]"
                              title="View Visits"
                            >
                              <Users className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResendClick(email)}
                              className="border-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]"
                            >
                              <Send className="mr-2 h-3 w-3" />
                              Resend
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#3f4816]">
                  <p className="text-sm text-[#888680]">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="border-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="border-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Resend Dialog */}
      <Dialog open={resendDialogOpen} onOpenChange={setResendDialogOpen}>
        <DialogContent className="bg-[#302f2c] border-[#3f4816] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#d9fb06]">Resend Email</DialogTitle>
            <DialogDescription className="text-[#888680]">
              Choose a campaign template to resend to {selectedEmail?.recipient}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-[#888680] mb-2">Recipient:</p>
              <p className="text-white font-medium">{selectedEmail?.recipient}</p>
            </div>

            <div>
              <p className="text-sm text-[#888680] mb-3">Select Campaign Template:</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {emailTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => {
                      setSelectedTemplate(template);
                      setContactName(''); // Reset contact name when template changes
                    }}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedTemplate?.id === template.id
                        ? 'border-[#d9fb06] bg-[#3f4816]'
                        : 'border-[#3f4816] bg-[#1a1c1b] hover:border-[#d9fb06] hover:bg-[#3f4816]'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-white">{template.name}</h3>
                        <p className="text-sm text-[#888680] mt-1">{template.description}</p>
                      </div>
                      <Badge className="bg-[#3f4816] text-[#d9fb06] border-[#3f4816] ml-2">
                        {template.category}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedTemplate?.requiresContactName && (
              <div className="space-y-2">
                <Label htmlFor="resendContactName" className="text-[#d9fb06]">
                  Contact Name <span className="text-[#888680]">(optional - will appear in greeting, e.g., "Hey, John!")</span>
                </Label>
                <Input
                  id="resendContactName"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="e.g. John Smith (leave blank for generic greeting)"
                  className="bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-[#3f4816]">
            <Button
              variant="outline"
              onClick={() => {
                setResendDialogOpen(false);
                setSelectedEmail(null);
                setSelectedTemplate(null);
                setContactName('');
              }}
              className="border-[#3f4816] text-[#888680] hover:bg-[#3f4816] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResend}
              disabled={!selectedTemplate || isResending}
              className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e005] font-semibold"
            >
              {isResending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Visits Dialog */}
      <Dialog open={visitsDialogOpen} onOpenChange={setVisitsDialogOpen}>
        <DialogContent className="bg-[#302f2c] border-[#3f4816] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#d9fb06]">Email Visits</DialogTitle>
            <DialogDescription className="text-[#888680]">
              Visits from {selectedEmailForVisits?.recipient}
            </DialogDescription>
          </DialogHeader>
          {selectedEmailForVisits && (
            <EmailVisits 
              sentEmailId={selectedEmailForVisits.id}
              onClose={() => {
                setVisitsDialogOpen(false);
                setSelectedEmailForVisits(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SentEmails;
